import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Modal, Select, SelectItem} from "@mantine/core";
import {useForm, yupResolver} from "@mantine/form";
import {DatePicker, TimeInput} from "@mantine/dates";
import {showNotification} from "@mantine/notifications";
import {Edit as EditIcon} from "tabler-icons-react";
import * as Yup from "yup";
import {IUpdateSubscriptionParams} from "../../../store/types";
import AdminApiClient from "../../../utils/http-clients/AdminApiClient";

type SubscriptionModalProps = {
    initialValues: DefaultValues;
    opened: boolean;
    onClosed: () => void;
    onUpdated?: () => void;
}

type DefaultValues = {
    company: string;
    plan: string;
    end_date: Date | null;
    end_time: Date | null;
}

const SubscriptionModal = ({initialValues, opened, onClosed, onUpdated}: SubscriptionModalProps) => {
    const {t} = useTranslation()
    const formRef = useRef<HTMLFormElement>(null)
    const [planOptions, setPlanOptions] = useState<SelectItem[]>([])
    const [plansLoading, setPlansLoading] = useState<boolean>(false)
    const [sending, setSending] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const adminApiClient = AdminApiClient.getInstance()
    const schema = Yup.object().shape({
        company: Yup.string().required(t('validation.required')),
        plan: Yup.string().required(t('validation.required')),
        end_date: Yup.date().required(t('validation.required')),
        end_time: Yup.date().required(t('validation.required'))
    })

    const parsedDate = initialValues.end_date ? new Date(initialValues.end_date) : null

    const form = useForm({
        schema: yupResolver(schema),
        initialValues: {
            company: initialValues.company,
            plan: initialValues.plan,
            end_date: parsedDate,
            end_time: parsedDate
        }
    })

    useEffect(() => {
        setLoading(plansLoading || sending)
    }, [plansLoading, sending]);

    useEffect(() => {
        (async () => {
            try {
                setPlansLoading(true)
                const {data} = await adminApiClient.getPlans()
                if (data.data) {
                    const options = data.data.map((plan: any) => ({
                        value: plan.id,
                        label: `${plan.name}`
                    }))
                    setPlanOptions(options)
                }
            } catch (error) {
                console.error('Error fetching plans:', error)
            } finally {
                setPlansLoading(false)
            }
        })()
    }, [adminApiClient])

    const handleSubmit = async (values: typeof form.values) => {
        const {plan, end_date, end_time} = values;

        const params: IUpdateSubscriptionParams = {}

        if (plan) {
            params.plan = plan;
        }

        if (end_date && end_time) {
            const combined = new Date(end_date);
            combined.setHours(end_time.getHours());
            combined.setMinutes(end_time.getMinutes());
            combined.setSeconds(end_time.getSeconds());

            params.end_date = combined.toISOString();
        }

        try {
            setSending(true);
            const {data} = await adminApiClient.updateSubscription(initialValues.company, params);
            showNotification({
                color: 'green',
                title: t('successful'),
                message: data.message
            })

            if (onUpdated) {
                onUpdated();
            }
        } catch (error: any) {
            console.error('Error updating subscription:', error);
            showNotification({
                color: 'red',
                title: t('unsuccessful'),
                message: error.response ? error.response.data.message : t('subscription_does_not_update')
            });
        } finally {
            setSending(false);
        }
    }

    return (
        <Modal centered opened={opened} title={t('update_subscription_plan')} onClose={onClosed}>
            <Divider size="xs" my="sm"/>
            <form ref={formRef} onSubmit={form.onSubmit(handleSubmit)} noValidate>
                <Grid>
                    <Grid.Col sm={12}>
                        <Select
                            label={t('plan')}
                            placeholder={t('select')}
                            data={planOptions}
                            searchable
                            nothingFound={t('errors.no_options')}
                            clearable
                            required
                            disabled={loading}
                            {...form.getInputProps('plan')}
                        />
                    </Grid.Col>
                    <Grid.Col sm={12}>
                        <Grid>
                            <Grid.Col sm={8}>
                                <DatePicker
                                    placeholder={t('end_date')}
                                    label={t('end_date')}
                                    inputFormat="YYYY-MM-DD"
                                    labelFormat="MM/YYYY"
                                    clearable
                                    required
                                    disabled={loading}
                                    {...form.getInputProps('end_date')}
                                />
                            </Grid.Col>
                            <Grid.Col sm={4}>
                                <TimeInput label="&nbsp;"
                                           withSeconds
                                           clearable
                                           disabled={loading}
                                           {...form.getInputProps('end_time')}/>
                            </Grid.Col>
                        </Grid>
                    </Grid.Col>
                    <Grid.Col sm={12}>
                        <Divider size="xs" my="sm"/>
                    </Grid.Col>
                    <Grid.Col sm={12}>
                        <Button type="submit"
                                leftIcon={<EditIcon size={12}/>}
                                color="blue"
                                disabled={loading}
                                loading={sending}
                                fullWidth>{t('content_actions.edit')}</Button>
                    </Grid.Col>
                </Grid>
            </form>
        </Modal>
    );
}

export default SubscriptionModal;
